import { useState } from 'react';
import type { Initiative, InitiativeFormField } from '../shared/types.ts';

export type InitiativeFormValues = Record<string, string | string[]>;

export function InitiativeForm({
  initiative,
  submitting,
  onSubmit,
  initialValues,
  submitLabel,
}: {
  initiative: Initiative;
  submitting: boolean;
  onSubmit: (values: InitiativeFormValues) => void;
  initialValues?: InitiativeFormValues;
  submitLabel?: string;
}) {
  const initial: InitiativeFormValues = {};
  for (const f of initiative.formFields) {
    const seed = initialValues?.[f.name];
    if (f.kind === 'multiselect') {
      initial[f.name] = Array.isArray(seed) ? seed : [];
    } else {
      initial[f.name] = typeof seed === 'string' ? seed : '';
    }
  }
  const [values, setValues] = useState<InitiativeFormValues>(initial);
  const [touched, setTouched] = useState(false);

  function setValue(name: string, v: string | string[]) {
    setValues((prev) => ({ ...prev, [name]: v }));
  }

  function isFieldEmpty(f: InitiativeFormField): boolean {
    const v = values[f.name];
    if (Array.isArray(v)) return v.length === 0;
    return !v || v.trim() === '';
  }

  const missing = initiative.formFields.filter((f) => f.required && isFieldEmpty(f));
  const valid = missing.length === 0;

  function handleSubmit() {
    setTouched(true);
    if (!valid) return;
    onSubmit(values);
  }

  return (
    <div className="px-4 pt-4 pb-4 space-y-5">
      <div>
        <p className="font-semibold text-lg leading-snug">{initiative.title}</p>
        <p className="mt-1 text-[13px] text-muted-foreground">{initiative.tagline}</p>
      </div>

      {initiative.formFields.length === 0 ? null : (
        initiative.formFields.map((field) => (
          <FormField
            key={field.name}
            field={field}
            value={values[field.name] ?? (field.kind === 'multiselect' ? [] : '')}
            error={touched && field.required && isFieldEmpty(field)}
            onChange={(v) => setValue(field.name, v)}
          />
        ))
      )}

      <button
        type="button"
        disabled={submitting}
        onClick={handleSubmit}
        className="w-full flex items-center justify-center h-12 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 active:scale-[0.98] transition-all disabled:opacity-60"
      >
        {submitting
          ? 'Sending…'
          : submitLabel ?? (initiative.formFields.length === 0 ? 'Count me in' : 'Submit')}
      </button>
    </div>
  );
}

function FormField({
  field,
  value,
  error,
  onChange,
}: {
  field: InitiativeFormField;
  value: string | string[];
  error: boolean;
  onChange: (v: string | string[]) => void;
}) {
  const labelEl = (
    <div>
      <label className="text-[13px] font-medium text-foreground">
        {field.label}
        {field.required && <span className="text-primary ml-0.5">*</span>}
      </label>
      {field.helpText && (
        <p className="text-[12px] text-muted-foreground mt-0.5">{field.helpText}</p>
      )}
    </div>
  );

  if (field.kind === 'text') {
    return (
      <div className="space-y-1.5">
        {labelEl}
        <input
          type="text"
          value={value as string}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder ?? undefined}
          className={`w-full h-11 px-3 rounded-lg border bg-card text-[15px] focus:outline-none focus:ring-2 focus:ring-primary/40 ${
            error ? 'border-destructive' : 'border-border'
          }`}
        />
      </div>
    );
  }

  if (field.kind === 'textarea') {
    return (
      <div className="space-y-1.5">
        {labelEl}
        <textarea
          value={value as string}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder ?? undefined}
          rows={3}
          className={`w-full px-3 py-2 rounded-lg border bg-card text-[15px] focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none ${
            error ? 'border-destructive' : 'border-border'
          }`}
        />
      </div>
    );
  }

  if (field.kind === 'select') {
    return (
      <div className="space-y-2">
        {labelEl}
        <div className="space-y-1.5">
          {(field.options ?? []).map((opt) => {
            const selected = value === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange(opt.value)}
                className={`w-full text-left px-4 py-3 rounded-lg border text-[14px] transition-all ${
                  selected
                    ? 'border-primary bg-primary/5 text-foreground font-medium'
                    : 'border-border bg-card text-foreground/80 hover:border-foreground/30'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // multiselect
  const arr = Array.isArray(value) ? value : [];
  return (
    <div className="space-y-2">
      {labelEl}
      <div className="flex flex-wrap gap-1.5">
        {(field.options ?? []).map((opt) => {
          const selected = arr.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(selected ? arr.filter((v) => v !== opt.value) : [...arr, opt.value]);
              }}
              className={`px-3 py-1.5 rounded-full border text-[13px] transition-all ${
                selected
                  ? 'border-primary bg-primary/10 text-primary font-medium'
                  : 'border-border bg-card text-foreground/70 hover:border-foreground/30'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
